/**
 * FE-specific shipping defaults for the Pixi Bundle checkout.
 *
 * The country allow-list (`HARDWARE_CHECKOUT_ALLOWED_COUNTRIES`) and the
 * country→bucket geography (`resolveCountryBucket` / `CountryBucket`) now come
 * from `@screencloud/billing-client-api` (5.4.0+) — import them from there
 * directly. This module only holds the FE's default preselected country, which
 * is a product decision rather than SDK data.
 */
import { HARDWARE_CHECKOUT_ALLOWED_COUNTRIES } from "@screencloud/billing-client-api";

/**
 * The country preselected in the "Ship to" dropdown / shipping form before the
 * user picks one. Must be a member of the SDK's
 * `HARDWARE_CHECKOUT_ALLOWED_COUNTRIES` allow-list.
 */
export const DEFAULT_HARDWARE_CHECKOUT_COUNTRY = "US";

/**
 * FE-only "Ship to" option for shipping destinations the hardware allow-list
 * does not cover (GRWBKK-1730). It is deliberately NOT a member of
 * `HARDWARE_CHECKOUT_ALLOWED_COUNTRIES` — selecting it makes the free-device
 * deal unavailable while the discount deal stays usable. Never sent to the
 * billing client or a rate lookup.
 */
export const OTHER_SHIPPING_COUNTRY = "OTHER";

/** Whether the "Ship to" selection is the FE-only unsupported-location option. */
export const isOtherShippingCountry = (
  countryCode: string | undefined,
): boolean => countryCode === OTHER_SHIPPING_COUNTRY;

/**
 * The "Ship to" country to preselect for the free-device deal, derived from the
 * Studio org country (`currentOrg.preferences.settings.country_code`) when
 * present. A supported org country is used as-is; an org in a location we can't
 * ship hardware to resolves to {@link OTHER_SHIPPING_COUNTRY} (so the deal shows
 * its unavailable state up front); with no org country we keep the historical
 * `US` default. No IP/geolocation lookup is used — only Studio org settings.
 */
export const resolveDefaultShippingCountry = (
  orgCountryCode: string | undefined,
): string => {
  const code = orgCountryCode?.trim().toUpperCase();
  if (!code) return DEFAULT_HARDWARE_CHECKOUT_COUNTRY;
  return (HARDWARE_CHECKOUT_ALLOWED_COUNTRIES as readonly string[]).includes(
    code,
  )
    ? code
    : OTHER_SHIPPING_COUNTRY;
};
